import { DslAggregations } from './types';
import { stripQuoteArtifacts } from '../../../library/util/transform';
import { sanitizeDslInput } from './sanitize';

/**
 * CHKM-4026: normalizes the raw `aggregations` block returned by the DSL search backend into the
 * `DslAggregations` shape consumed by `FacetsPanel` and `combineDslWithFacets`.
 *
 * The backend's real contract mixes two aggregation shapes under one object: a scalar
 * `uniq_<field>` cardinality count (e.g. `uniq_name: 37`) alongside a `bucket_<field>` array of
 * `{ doc_count, key }` bucket objects (e.g. `bucket_name: [{ doc_count: 69, key: 'jeff bezos' }]`).
 * The old code assumed every value in the aggregations object was a bucket array and called
 * `.map()` on it unconditionally, which threw (and white-screened the page) the moment a scalar
 * `uniq_*` key was present. This function only accepts array-valued entries and silently drops
 * everything else, so it can never throw regardless of what shape the backend sends.
 *
 * The `bucket_` prefix is stripped from the group key because that key doubles as the DSL field
 * name — `combineDslWithFacets` inserts it verbatim into a `<field>: equals "<value>"` clause, so
 * the group key must be the raw field name (`name`), not the backend's wire key (`bucket_name`).
 * Two raw keys can normalize to the same group (e.g. `bucket_name` and a bare `name`) — those are
 * merged into one group rather than the later key silently overwriting the former.
 *
 * CHKM-4009 (D2): the backend also emits `pair_<A>_<B>` cross-tabulation aggregation groups (e.g.
 * `pair_worksFor_name`) — a matrix of co-occurrence counts between two fields, not a facet on a
 * single field. These are array-valued just like a real `bucket_*` group, so without an explicit
 * denylist they were rendered as selectable facets that always returned 0 hits once clicked (the
 * backend has no field named `pair_worksFor_name` to filter on). The `pair_` check runs against the
 * normalized `groupKey` (post `bucket_` strip), not the raw wire key — a wire key of
 * `bucket_pair_a_b` strips to `pair_a_b`, which must still be caught; checking the raw key alone
 * would miss that shape (PR #503 round 2 fix — the raw-key check that shipped in round 1 missed it).
 *
 * PR #503 round 2 (security): the group **key** is sanitized through the same `sanitizeDslInput`
 * used for values (see the round 1 note below) — it is rendered as the group label in
 * `FacetsPanel` and spliced unescaped into the outbound DSL clause in `combineDslWithFacets`, so an
 * unsanitized key is exactly as exploitable as an unsanitized value. A key that sanitizes to empty
 * is dropped, same as the pre-existing `bucket_`-only case.
 *
 * CHKM-4009 (D1): a bucket's `key` can arrive as a SAMS Python-repr artifact — a value wrapped in
 * stray quotes (`"amazon"`), or, worse, a stringified Python list-repr (`"['a', 'b']"`) that is not
 * a single facet value at all. `stripQuoteArtifacts` (shared with the rest of the app's SAMS
 * artifact cleanup) is reused here to strip that wrapping before the value is ever inserted into a
 * DSL clause. The BACKEND grammar's quoted-value syntax has no in-quote escape mechanism we can
 * verify for a stray `"` or `\` left over after stripping (see `canEncodeDslFacetValue` below for
 * why this is a backend, not client, constraint), so any value that still contains one of those
 * characters is dropped rather than sent — better to omit one facet chip than to send an HTTP 400
 * (or a query that will never match) for the entire search.
 *
 * PR #503 round 1 (security): every character the *user* types into the DSL search box is scrubbed
 * by `sanitizeDslInput` (wired at `DslSearchBar.tsx`) before it ever reaches the parser/state — but
 * a facet value originates from the backend and, until this change, was spliced verbatim into both
 * the rendered pill and the outbound DSL query, bypassing that scrubbing entirely. A control
 * character or a bidi override/formatting character (the "Trojan Source" vector `sanitizeDslInput`
 * exists to close) would sail through unmodified. `toFacetValue` below now runs every bucket value
 * through the same `sanitizeDslInput`, after `stripQuoteArtifacts` (stripping can expose characters
 * — e.g. one that had been sitting inside a now-removed quote wrapper — that the sanitizer must
 * then see and remove), and trims the result. A value that sanitizes to nothing (e.g. one made up
 * entirely of control characters) is dropped by the same `!facetValue` guard used for the
 * list-repr case. `sanitizeDslInput` also hard-caps the result at `DSL_MAX_LENGTH` as a side effect
 * of reuse — harmless (arguably beneficial) here, since a bucket value longer than the entire
 * allowed query could never be a useful facet chip anyway, but called out so it isn't later misread
 * as a bug.
 *
 * PR #503 round 1 (Item 4): buckets that collapse to the same cleaned value (e.g. `amazon` and
 * `"amazon"`) are merged rather than kept as separate pills (both to avoid a misleading duplicate
 * chip and a duplicate-React-key hazard in `FacetsPanel`, where `key={value}` would otherwise
 * collide). The survivor keeps the count belonging to whichever raw bucket needed no
 * stripping/sanitizing at all — i.e. was already exactly the value being offered as the facet —
 * falling back to first-seen order if neither (or both) qualify. Counts are deliberately NOT
 * summed: if the backend indexes the artifact form and the clean form as two distinct values, only
 * the clean form's count reflects what `<field>: equals "<value>"` will actually match, and summing
 * would advertise a chip that over-promises how many documents it narrows to.
 */
const PAIR_KEY_PREFIX = 'pair_';

/**
 * A cleaned facet value is only safe to encode into the DSL grammar's quoted-value syntax
 * (`field: equals "value"`) if it contains neither a double quote nor a backslash. This is a
 * constraint of the BACKEND grammar, which this repo has no way to verify supports any in-quote
 * escape convention (unresolved — see the tech spec's open questions, owner: the CHKM-3974 backend
 * team) — it is NOT a client-side limitation. The client tokenizer plainly does support escaping
 * inside a quoted value (`tokenizer.ts` consumes `\\`/`\"`), and `escapeDslValue` (`facets.ts`)
 * already emits exactly that convention — do not "fix" this predicate by touching `escapeDslValue`,
 * which the spec explicitly locks as-is.
 *
 * One consequence of that is worth recording here: `stripQuoteArtifacts` un-escapes `\"` -> `"` as
 * its first step (see its own doc comment), so a bucket value that arrived from the backend
 * *already correctly escaped* is converted into an unencodable one and silently dropped by this
 * check — e.g. `The \"Washington\" Post` loses its chip with no UI signal. This is an accepted
 * known limitation pending confirmation of a backend escape convention, not a bug in this file.
 */
const canEncodeDslFacetValue = (value: string): boolean => !value.includes('"') && !value.includes('\\');

/**
 * Strips SAMS Python-repr quote artifacts from a raw bucket value, then runs the result through
 * `sanitizeDslInput` (see the module doc comment above for why) and trims it.
 *
 * Returns `null` when `stripQuoteArtifacts` parses the raw value as a Python list-repr (returns a
 * `string[]`) — a bucket `key` is always a single facet value, never a list, so that shape is never
 * usable as-is and must be dropped by the caller's `!facetValue` guard rather than falling back to
 * the raw (still single-quoted, and therefore still DSL-encodable) list-repr string — or when
 * sanitizing/trimming leaves nothing at all (a value that was only control characters or invisible
 * formatting), for the same reason.
 */
const toFacetValue = (raw: string): string | null => {
    const stripped = stripQuoteArtifacts(raw);
    if (Array.isArray(stripped)) {
        return null;
    }
    const sanitized = sanitizeDslInput(stripped).trim();
    return sanitized || null;
};

export const normalizeDslAggregations = (raw: unknown): DslAggregations | null => {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
        return null;
    }

    // Keyed by the normalized group name (post `bucket_` strip), not the raw wire key, so that two
    // raw keys which normalize to the same group (e.g. `bucket_name` and a bare `name`) accumulate
    // into one group instead of the second one silently overwriting the first.
    const groups = new Map<
        string,
        {
            buckets: { value: string; count: number }[];
            indexByValue: Map<string, number>;
            cleanByValue: Map<string, boolean>;
        }
    >();

    Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
        if (!Array.isArray(value)) {
            return;
        }

        const strippedKey = key.startsWith('bucket_') ? key.slice('bucket_'.length) : key;
        const groupKey = sanitizeDslInput(strippedKey).trim();

        if (!groupKey || groupKey.startsWith(PAIR_KEY_PREFIX)) {
            return;
        }

        let group = groups.get(groupKey);
        if (!group) {
            group = { buckets: [], indexByValue: new Map(), cleanByValue: new Map() };
            groups.set(groupKey, group);
        }
        const { buckets, indexByValue, cleanByValue } = group;

        value.forEach((item) => {
            if (typeof item !== 'object' || item === null) {
                return;
            }
            const rawValue = (item as Record<string, unknown>).key ?? (item as Record<string, unknown>).value;
            if (typeof rawValue !== 'string' && typeof rawValue !== 'number') {
                return;
            }
            const bucketValue = String(rawValue);
            if (!bucketValue) {
                return;
            }

            const facetValue = toFacetValue(bucketValue);
            if (!facetValue || !canEncodeDslFacetValue(facetValue)) {
                return;
            }

            const rawCount = (item as Record<string, unknown>).doc_count ?? (item as Record<string, unknown>).count;
            const count = Number(rawCount);
            const safeCount = Number.isFinite(count) ? count : 0;
            // "Clean" means the raw value needed no artifact-stripping or sanitizing at all — it was
            // already exactly the value being offered as the facet. Used below to decide which
            // bucket's count survives a merge.
            const isClean = facetValue === bucketValue;

            const existingIndex = indexByValue.get(facetValue);
            if (existingIndex !== undefined) {
                // Two buckets collapsed to the same facet value — either the same raw key emitted
                // both an artifact and clean form, or two differently-prefixed raw keys normalized
                // to the same group. Keep the count belonging to whichever bucket is clean (see the
                // module doc comment for why summing is wrong); fall back to first-seen if neither
                // (or both) qualify.
                const wasClean = cleanByValue.get(facetValue) ?? false;
                if (isClean && !wasClean) {
                    buckets[existingIndex] = { value: facetValue, count: safeCount };
                    cleanByValue.set(facetValue, true);
                }
            } else {
                indexByValue.set(facetValue, buckets.length);
                cleanByValue.set(facetValue, isClean);
                buckets.push({ value: facetValue, count: safeCount });
            }
        });
    });

    const result: DslAggregations = {};
    groups.forEach((group, groupKey) => {
        if (group.buckets.length) {
            // PR #503 round 2 (Item 7): a merge above can replace a bucket's count in place without
            // moving its position (see the merge branch above), which can leave the group out of the
            // backend's original descending-count order once a clean-form count is substituted for an
            // artifact-form one. Re-sort by count descending here, once per group, after every merge
            // has resolved, so display order always reflects the final counts.
            result[groupKey] = [...group.buckets].sort((a, b) => b.count - a.count);
        }
    });

    return Object.keys(result).length ? result : null;
};
